import {z} from 'zod'
import {PaginationResponse} from '@/types'

// Extra data schema for notifications
export const NotificationExtraData = z.object({
    patientName: z.string().optional(),
    projectCode: z.string().optional(),
    // The conversation's title on Conv-00x notifications — the BE sends it as `title`.
    title: z.string().optional(),
    // The conversation's id on Conv-00x notifications: enables opening the internal
    // conversations drawer when the notification has no link (patient-less conversation).
    // Optional forever: notifications persisted before the BE addition never carry it.
    conversationId: z.string().optional()
})

export type NotificationExtraData = z.infer<typeof NotificationExtraData>

// Notification types enum
export const NotificationType = z.enum([
    'Paz-001',
    'Paz-002',
    'Paz-003',
    'Paz-004',
    'Paz-005',
    'RT-001',
    'RT-002',
    'RT-003',
    'Pri-001',
    'Pri-002',
    'Pri-003',
    'Pri-004',
    'Pri-005',
    'Pri-006',
    'Pri-007',
    'Age-001',
    'Age-001-Repeated',
    'Age-002',
    'Age-003',
    'Age-004',
    'Age-005',
    'Age-006',
    'Age-007',
    'Age-008',
    'Age-009',
    'Age-010',
    'Age-011',
    'Imp-Per-001',
    'Conv-001',
    'Conv-002',
    'Conv-003'
])

export type NotificationType = z.infer<typeof NotificationType>

// Notification severity enum
export const NotificationSeverity = z.enum(['info', 'alert'])

export type NotificationSeverity = z.infer<typeof NotificationSeverity>

// The actual notification object from the API
export const Notification = z.object({
    id: z.string(),
    type: NotificationType,
    title: z.string(),
    details: z.unknown().nullable(),
    bodyTemplate: z.string(),
    severity: NotificationSeverity,
    link: z.string().nullable(),
    triggeredBy: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    scheduledAt: z.coerce.date().nullable(),
    sentAt: z.coerce.date(),
    extraData: NotificationExtraData.nullable(),
    triggeredByUser: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string()
    })
})

export type Notification = z.infer<typeof Notification>

// Socket notification schema aligned with the original Notification model
export const SocketNotification = z.object({
    id: z.string(),
    title: z.string(),
    bodyTemplate: z.string().optional(),
    body: z.string().optional(),
    type: NotificationType,
    severity: NotificationSeverity,
    link: z.string().nullable(),
    triggeredBy: z.string().nullable(),
    createdAt: z.coerce.date(),
    sentAt: z.coerce.date().optional(),
    extraData: NotificationExtraData.nullish(),
    conversationId: z.string().nullish()
})

export type SocketNotification = z.infer<typeof SocketNotification>

// The UserNotification from the API matches this structure
export const UserNotification = z.object({
    id: z.string(),
    userId: z.string(),
    notificationId: z.string(),
    conversationId: z.string().nullable(),
    readAt: z.coerce.date().nullable(),
    details: z.unknown().nullable(),
    notification: Notification
})

export type UserNotification = z.infer<typeof UserNotification>

// Create a flattened version of UserNotification for internal use
export const NotificationsResponse = z.object({
    meta: PaginationResponse.extend({
        firstPage: z.number(),
        firstPageUrl: z.string(),
        lastPageUrl: z.string(),
        nextPageUrl: z.string().nullable(),
        previousPageUrl: z.string().nullable()
    }),
    data: z.array(UserNotification)
})

export type NotificationsResponse = z.infer<typeof NotificationsResponse>

export const NotificationCount = z.object({
    toRead: z.number(),
    total: z.number()
})

export type NotificationCount = z.infer<typeof NotificationCount>
